import { GET_AI_COMMANDS_BY_SESSION_ID } from '@/hooks/query/useQueryAICommandsBySessionId';
import { GET_BACKUP_AUDIO } from '@/hooks/query/useQueryBackupAudio';
import { GET_DOCUMENT_AI_COMMANDS_BY_SESSION_ID } from '@/hooks/query/useQueryDocumentAICommandsBySessionId';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  GET_SESSION_BY_ID,
  GET_SESSION_CONSULT_NOTE_TIMELINE,
} from '@heidi/session/contracts';

/** Return the client's original promise without adding a settlement tick. */
export function invalidateSessionCache(sessionId: string): Promise<void> {
  return reactQueryClient.invalidateQueries({
    queryKey: [GET_SESSION_BY_ID, { sessionId }],
  });
}

/** Fire-and-forget in the original order, with no handlers attached. */
export function invalidateAiCommandsAfterClose(sessionId: string): void {
  void reactQueryClient.invalidateQueries({
    queryKey: [GET_DOCUMENT_AI_COMMANDS_BY_SESSION_ID, { sessionId }],
  });
  void reactQueryClient.invalidateQueries({
    queryKey: [GET_AI_COMMANDS_BY_SESSION_ID, { sessionId }],
  });
}

export function invalidateNoteHistoryAfterClose(sessionId: string): void {
  // Generation just produced a new timeline version (note_created /
  // note_regenerated). Refresh the consult-note timeline so version
  // history surfaces without waiting for a later refetch (DS-694).
  void reactQueryClient.invalidateQueries({
    queryKey: [GET_SESSION_CONSULT_NOTE_TIMELINE, { sessionId }],
  });

  // Dictation playback: the recordings list is the ONE thing on a dictate
  // session that a stop leaves stale. `DictationPlayback` mounts at
  // dictate START (`transcribe_mode` is optimistic), so its first fetch is
  // always empty, and every later dictation in the same session appends a
  // recording the cache can't know about. The upload mutation's own
  // `onSuccess` invalidation is the fast path, but it is missed whenever
  // the stop never reaches the upload, the upload fails terminally / trips
  // the APP-7239 circuit breaker, or its refetch loses the
  // read-after-write race — and the global 1h `staleTime` then pins the
  // stale list until a hard reload.
  //
  // Stream close is the better-timed second trigger: the backup POST is
  // fired at the stop and note generation streams for seconds afterwards,
  // so by the time we get here the recording has almost always landed.
  // Unconditional on purpose — for a transcribe session there is no cache
  // entry to invalidate (the component never mounts, and the endpoint 400s
  // for non-dictate sessions), so this is a no-op rather than a wasted
  // request.
  void reactQueryClient.invalidateQueries({
    queryKey: [GET_BACKUP_AUDIO, { sessionId }],
  });
}
